import React, { useState } from 'react';
import { useFiltriEsperienze, nomeLeggibile } from '../../features/knowledge-management/hooks/useTassonomie.js';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import { STATE_LABELS } from './kmLabels.js';

/* ═══════════════════════════════════════════════════════════
   Modifica anagrafica e dati commerciali della risorsa — UC-CD-02.

   SIT-031 (28/09): il form "Modifica candidato" esistente
   (CandidateEditModal → ChatInterface/MetadataManager) è un editor di
   metadati generico (PUT /api/candidates/{cvid} → {metadata}) e non è mai
   stato collegato a PATCH /resources/{id}, che il backend espone già
   (targets.update_resource, CAMPI_MODIFICABILI). Questo componente è la UI
   mancante: nuovo, separato, non tocca l'editor metadati esistente.

   GET /resources/{id} precompila; PATCH /resources/{id} scrive solo i campi
   di CAMPI_MODIFICABILI (nome, email, wa_phone, regione, certificazioni,
   lingue, costo_attuale, costo_unita, costo_valuta,
   tariffa_listino_onsite, tariffa_listino_remoto,
   disponibilita_contrattuale, partita_iva, note_libere). Il backend ignora i
   campi con valore null nel payload (non li sovrascrive): per questo i
   numeri vuoti e le stringhe vuote non toccate restano null, mentre le
   liste (certificazioni/lingue) vengono sempre inviate come array — anche
   vuoto, per permettere di svuotarle esplicitamente.

   28/09 (seconda passata, su richiesta): l'AF (§6, Entità e attributi,
   p.11) tratta "data di ultimo aggiornamento a cura del recruiter" come
   attributo di primo piano della Risorsa, distinto dal timer SA — ma
   prima di questa modifica non compariva mai a schermo, benché il backend
   la calcoli e la restituisca (aggiornato_da_recruiter_il/
   aggiornato_da_recruiter). Aggiunta una riga sempre visibile con
   quell'informazione, e — poiché il backend non tiene uno storico dei
   valori per l'anagrafica (record_history è usato solo dal Knowledge
   Writer per le schede esperienza, mai da update_resource; audit_log
   registra solo i nomi dei campi toccati, non i valori) — un riepilogo
   del confronto prima/dopo calcolato qui in locale, mostrato subito dopo
   il salvataggio. Non è persistente: chi riapre la scheda domani non lo
   rivede. Per uno storico persistente serve un intervento backend
   (arricchire i metadata già scritti da audit.log_event con i valori,
   non solo i nomi campo) — proposto ma non fatto qui, fuori perimetro FE.

   28/09 (terza passata, bug reale trovato testando a schermo): la
   risposta di GET /resources/{id} (targets.get_resource) non è piatta.
   Tutti i campi anagrafici/commerciali — compresi
   aggiornato_da_recruiter_il/aggiornato_da_recruiter — stanno annidati
   sotto la chiave "anagrafica" (`return dettaglio`, con
   `dettaglio["anagrafica"] = anagrafica`), non alla radice dell'oggetto.
   Solo stato_ciclo_vita/sottostato sono duplicati alla radice apposta per
   ResourceStateManager. Il precompile leggeva finora `data.<campo>`
   invece di `data.anagrafica.<campo>`: non ha mai funzionato, da quando
   il componente è stato scritto — mascherato dal fatto che si scriveva
   comunque a mano in campi che sembravano vuoti per la stessa ragione
   sbagliata. Corretto qui.

   28/09 (quarta passata, SIT-023): UC-CD-01, flusso alternativo 1 (AF
   p.32): per risorsa in R1 Inbound o R4 Da cancellare, "il dettaglio è
   integralmente in sola lettura" e "l'azione di modifica" non deve essere
   disponibile, con indicazione dello stato che la motiva.
   ResourceStateManager e ResourceSelfAssessmentPanel già si comportavano
   così (nessuna transizione proposta, invio SA bloccato con messaggio);
   questo componente no — mostrava "Modifica dati anagrafici" sempre.
   Aggiunto lo stesso gating qui, leggendo stato_ciclo_vita dalla radice
   della risposta (non da "anagrafica" — solo stato_ciclo_vita/sottostato
   sono duplicati lì, per lo stesso motivo per cui ResourceStateManager li
   legge dalla radice).

   08/10 (UAT-06): i campi di `candidato` nascono vuoti — HR non li ha mai
   compilati — mentre il CV Altamira li porta già. GET /resources/{id}
   restituisce ora il blocco in sola lettura `da_cv` (valori del CV mappati
   sui nomi dei campi qui sotto). Il form lo usa come valore iniziale SOLO
   dove il campo di candidato è vuoto, e lo segnala con «dal CV, da
   confermare». La baseline del confronto prima/dopo resta il valore di
   candidato: il salvataggio rende effettivi i valori dal CV e il riepilogo
   li elenca come cambiati. I costi del CV che non sono numeri (es. «RAL
   40k») arrivano come `<campo>_testo`/`costo_testo` e sono mostrati come
   suggerimento sotto il campo numerico, che resta vuoto.
   Partita IVA: prima, se ignota, veniva forzata a false e ogni
   salvataggio scriveva «No». Ora è a tre valori (Sì / No / Non indicato):
   «Non indicato» è null e buildPayload non la invia.

   16/10 — Seniority modificabile da HR (Armando, c2c6797, RF-KM-16): è un
   dato del PROFILO, non del candidato. Si legge da `profilo.seniority` di
   GET /resources/{id} e si scrive con lo stesso PATCH /resources/{id}
   (campo `seniority`, valori junior | mid | senior | lead | manager).
   Tendina con «Non indicata»: la seniority si invia SOLO se HR l'ha
   cambiata, così un salvataggio dell'anagrafica non tocca il profilo.

   16/10 — UC-CD-02 completato: anche COMPETENZE e SETTORI sono modificabili
   da HR. Come la seniority sono dati del profilo versionato (targets.
   CAMPI_PROFILO: competenze → profilo.skill, settori → profilo.settori,
   entrambi elenchi di testo): si leggono da `profilo` e si inviano nel PATCH
   solo se cambiati; ogni modifica crea una nuova versione del profilo.
   Settori: selezione per NOME dalla tassonomia di GET /esperienze/filtri
   (primo e secondo livello), mai codici a schermo; un codice salvato che non
   è in tassonomia resta nel valore e non viene perso.
═══════════════════════════════════════════════════════════ */

const FIELD_LABELS = {
  nome: 'Nome',
  email: 'Email',
  wa_phone: 'Telefono / WhatsApp',
  regione: 'Regione',
  certificazioni: 'Certificazioni (separate da virgola)',
  lingue: 'Lingue (separate da virgola)',
  costo_attuale: 'Costo attuale',
  costo_unita: 'Unità costo',
  costo_valuta: 'Valuta costo',
  tariffa_listino_onsite: 'Tariffa listino onsite',
  tariffa_listino_remoto: 'Tariffa listino remoto',
  disponibilita_contrattuale: 'Disponibilità contrattuale',
  note_libere: 'Note libere',
};

// Valori ammessi dal backend (targets.SENIORITY), con etichetta leggibile.
const SENIORITY_OPZIONI = [
  ['junior', 'Junior'],
  ['mid', 'Mid'],
  ['senior', 'Senior'],
  ['lead', 'Lead'],
  ['manager', 'Manager'],
];
const SENIORITY_ETICHETTA = Object.fromEntries(SENIORITY_OPZIONI);

// UC-CD-02 (16/10): competenze e settori del profilo.
function listaTesti(v) {
  if (Array.isArray(v)) return v.map((x) => String(x ?? '').trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((x) => x.trim()).filter(Boolean);
  return [];
}
const normCodice = (c) => String(c || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
function stessiTesti(a, b) {
  const k = (l) => l.map((x) => x.toLowerCase()).sort().join('|');
  return k(a) === k(b);
}
function stessiCodici(a, b) {
  const k = (l) => Array.from(new Set(l.map(normCodice))).sort().join('|');
  return k(a) === k(b);
}

// Selezione dei settori per nome: primo livello e, aperto, il secondo.
function SelettoreSettori({ albero, valori, onChange }) {
  const [aperti, setAperti] = useState(() => new Set());
  const scelti = new Set((valori || []).map(normCodice));
  const inAlbero = new Set();
  (albero || []).forEach((l1) => {
    inAlbero.add(normCodice(l1.codice));
    (l1.livello_2 || []).forEach((l2) => inAlbero.add(normCodice(l2.codice)));
  });
  const fuoriTassonomia = (valori || []).filter((c) => !inAlbero.has(normCodice(c)));

  function alterna(codice) {
    const k = normCodice(codice);
    const resto = (valori || []).filter((c) => normCodice(c) !== k);
    onChange(scelti.has(k) ? resto : [...resto, codice]);
  }
  function alternaApertura(codice) {
    setAperti((prev) => {
      const n = new Set(prev);
      if (n.has(codice)) n.delete(codice); else n.add(codice);
      return n;
    });
  }

  if (!Array.isArray(albero)) {
    return <div style={{ fontSize: '12px', color: 'var(--ds-txt-2)' }}>Caricamento dei settori…</div>;
  }
  return (
    <div>
      <div style={{ maxHeight: '220px', overflowY: 'auto', border: '0.5px solid var(--ds-border)', borderRadius: 'var(--ds-radius-sm, 6px)', padding: '6px 8px' }}>
        {albero.map((l1) => {
          const figli = l1.livello_2 || [];
          const figliScelti = figli.filter((l2) => scelti.has(normCodice(l2.codice))).length;
          const aperto = aperti.has(l1.codice) || figliScelti > 0;
          return (
            <div key={l1.codice} style={{ marginBottom: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px' }}>
                <input
                  type="checkbox"
                  id={`settore-${l1.codice}`}
                  checked={scelti.has(normCodice(l1.codice))}
                  onChange={() => alterna(l1.codice)}
                />
                <label htmlFor={`settore-${l1.codice}`} style={{ fontWeight: 600 }}>{l1.nome}</label>
                {figli.length > 0 && (
                  <button
                    type="button"
                    onClick={() => alternaApertura(l1.codice)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11.5px', color: 'var(--ds-txt-2)' }}
                  >
                    {aperto ? '▾' : '▸'} {figliScelti > 0 ? `${figliScelti} di ${figli.length}` : `${figli.length} sottosettori`}
                  </button>
                )}
              </div>
              {aperto && figli.map((l2) => (
                <div key={l2.codice} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', marginLeft: '22px' }}>
                  <input
                    type="checkbox"
                    id={`settore-${l2.codice}`}
                    checked={scelti.has(normCodice(l2.codice))}
                    onChange={() => alterna(l2.codice)}
                  />
                  <label htmlFor={`settore-${l2.codice}`}>{l2.nome}</label>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {fuoriTassonomia.length > 0 && (
        <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--ds-txt-2)' }}>
          {fuoriTassonomia.length === 1 ? '1 settore salvato non è' : `${fuoriTassonomia.length} settori salvati non sono`} nella tassonomia attuale: {fuoriTassonomia.length === 1 ? 'viene mantenuto' : 'vengono mantenuti'}.
        </div>
      )}
    </div>
  );
}

const NUMERIC_FIELDS = new Set(['costo_attuale', 'tariffa_listino_onsite', 'tariffa_listino_remoto']);
const LIST_FIELDS = new Set(['certificazioni', 'lingue']);

// Chiave di `da_cv` con il testo originale del CV quando il costo non è un
// numero convertibile (UAT-06).
const CV_TEXT_HINT = {
  costo_attuale: 'costo_testo',
  tariffa_listino_onsite: 'tariffa_listino_onsite_testo',
  tariffa_listino_remoto: 'tariffa_listino_remoto_testo',
};

// 15/10 — FE-02 (SIT-031): validazione dei formati prima del salvataggio.
// I campi errati sono evidenziati, i dati restano nel modulo e nulla parte
// verso il backend finché ci sono errori.
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const RE_TELEFONO = /^\+?[0-9][0-9\s]{5,19}$/;
function validaCampi(form) {
  const errori = {};
  const testo = (k) => String(form[k] ?? '').trim();
  if (testo('email') && !RE_EMAIL.test(testo('email'))) errori.email = 'Indirizzo email non valido (es. nome@dominio.it).';
  if (testo('wa_phone') && !RE_TELEFONO.test(testo('wa_phone'))) errori.wa_phone = 'Numero non valido: solo cifre e spazi, eventualmente preceduti da +.';
  for (const k of NUMERIC_FIELDS) {
    const v = testo(k).replace(',', '.');
    if (!v) continue;
    const n = Number(v);
    if (!Number.isFinite(n)) errori[k] = 'Inserire un importo numerico.';
    else if (n < 0) errori[k] = 'L\u2019importo non può essere negativo.';
  }
  if (testo('competenze') && testo('competenze').split(',').some((x) => !x.trim())) {
    errori.competenze = 'Elenco separato da virgole senza voci vuote (es. «Analisi funzionale, SQL»).';
  }
  for (const k of LIST_FIELDS) {
    const v = testo(k);
    if (v && v.split(',').some((x) => !x.trim())) errori[k] = 'Elenco separato da virgole senza voci vuote (es. «Inglese, Francese»).';
  }
  return errori;
}

function isEmptyValue(value) {
  if (Array.isArray(value)) return value.length === 0;
  return value === null || value === undefined || value === '';
}

function toEditable(value, key) {
  if (LIST_FIELDS.has(key)) return Array.isArray(value) ? value.join(', ') : (value || '');
  if (value === null || value === undefined) return '';
  return value;
}

function buildPayload(form) {
  const payload = {};
  for (const key of Object.keys(FIELD_LABELS)) {
    const raw = form[key];
    if (LIST_FIELDS.has(key)) {
      payload[key] = String(raw ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      continue;
    }
    if (NUMERIC_FIELDS.has(key)) {
      payload[key] = raw === '' || raw === null || raw === undefined ? null : Number(String(raw).replace(',', '.'));
      continue;
    }
    payload[key] = raw === '' ? null : raw ?? null;
  }
  if (typeof form.partita_iva === 'boolean') payload.partita_iva = form.partita_iva;
  return payload;
}

function displayValue(key, value) {
  if (key === 'seniority') return value ? (SENIORITY_ETICHETTA[value] || value) : '(non indicata)';
  if (key === 'competenze' || key === 'settori') {
    const arr = listaTesti(value);
    return arr.length ? arr.join(', ') : '(vuoto)';
  }
  if (key === 'partita_iva') return value === true ? 'Sì' : value === false ? 'No' : '(non indicato)';
  if (LIST_FIELDS.has(key)) {
    const arr = Array.isArray(value) ? value : String(value ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    return arr.length ? arr.join(', ') : '(vuoto)';
  }
  if (value === null || value === undefined || value === '') return '(vuoto)';
  return String(value);
}

// Confronta lo snapshot caricato prima della modifica con i valori
// effettivamente inviati nel PATCH, e restituisce solo i campi cambiati.
function diffChanges(before, sentPayload) {
  const changes = [];
  for (const key of Object.keys(FIELD_LABELS)) {
    const beforeNorm = LIST_FIELDS.has(key)
      ? String(before[key] ?? '').split(',').map((s) => s.trim()).filter(Boolean).join(',')
      : (before[key] ?? '');
    const afterRaw = sentPayload[key];
    const afterNorm = LIST_FIELDS.has(key)
      ? (Array.isArray(afterRaw) ? afterRaw.join(',') : '')
      : (afterRaw ?? '');
    if (String(beforeNorm) !== String(afterNorm)) {
      changes.push({ key, label: FIELD_LABELS[key], before: before[key], after: afterRaw });
    }
  }
  if (typeof sentPayload.partita_iva === 'boolean' && before.partita_iva !== sentPayload.partita_iva) {
    changes.push({ key: 'partita_iva', label: 'Titolare di partita IVA', before: before.partita_iva, after: sentPayload.partita_iva });
  }
  return changes;
}

function formatTimestamp(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
}

export default function ResourceAnagraficaEditor({ resourceId }) {
  const kmClient = useRealKmClient();
  const { filtri } = useFiltriEsperienze();

  const [status, setStatus] = useState('idle'); // idle | loading | ready | not_tracked | error
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null); // { il, da }
  const [changesSummary, setChangesSummary] = useState(null);
  const [cicloVita, setCicloVita] = useState(null); // stato_ciclo_vita, radice della risposta
  // UAT-06: blocco `da_cv` della risposta e campi del form precompilati da lì
  // (non ancora confermati da HR).
  const [daCv, setDaCv] = useState(null);
  const [fromCv, setFromCv] = useState(() => new Set());

  // Snapshot dei valori così come sono stati caricati dal server, usato
  // solo per calcolare il confronto prima/dopo al momento del salvataggio.
  const baselineRef = React.useRef({});

  const loadResource = React.useCallback(() => {
    if (!resourceId) return;
    setStatus('loading');
    setResult(null);
    kmClient
      .get(`/resources/${encodeURIComponent(resourceId)}`)
      .then((data) => {
        // I campi anagrafici/commerciali (e le due date di aggiornamento)
        // stanno annidati sotto "anagrafica", non alla radice della
        // risposta — vedi nota in testa al file (28/09, terza passata).
        const anagrafica = data?.anagrafica || {};
        const cv = data?.da_cv || null;
        // Baseline = valori di candidato: è contro questi che si calcola il
        // confronto, così i valori presi dal CV risultano «cambiati» e il
        // salvataggio li rende effettivi (UAT-06).
        const baseline = {};
        for (const key of Object.keys(FIELD_LABELS)) baseline[key] = toEditable(anagrafica[key], key);
        baseline.partita_iva = typeof anagrafica.partita_iva === 'boolean' ? anagrafica.partita_iva : null;
        // Seniority: dato del profilo (16/10), non dell'anagrafica.
        baseline.seniority = data?.profilo?.seniority || '';
        // UC-CD-02 (16/10): competenze (profilo.skill) e settori del profilo.
        baseline.competenze = listaTesti(data?.profilo?.skill).join(', ');
        baseline.settori = listaTesti(data?.profilo?.settori);

        // Form = candidato, e il CV solo dove il campo di candidato è vuoto.
        const next = { ...baseline };
        const daCvKeys = new Set();
        if (cv) {
          for (const key of [...Object.keys(FIELD_LABELS), 'partita_iva']) {
            if (isEmptyValue(anagrafica[key]) && !isEmptyValue(cv[key])) {
              next[key] = key === 'partita_iva' ? cv[key] : toEditable(cv[key], key);
              daCvKeys.add(key);
            }
          }
        }
        setForm(next);
        baselineRef.current = baseline;
        setDaCv(cv);
        setFromCv(daCvKeys);
        setLastUpdate({ il: anagrafica.aggiornato_da_recruiter_il ?? null, da: anagrafica.aggiornato_da_recruiter ?? null });
        setCicloVita(data?.stato_ciclo_vita ?? null);
        setStatus('ready');
      })
      .catch((err) => {
        if (err?.status === 404) {
          setStatus('not_tracked');
        } else {
          setResult({ ok: false, message: err?.detail || 'Errore nel caricamento dei dati anagrafici.' });
          setStatus('error');
        }
      });
  }, [kmClient, resourceId]);

  React.useEffect(() => {
    setEditing(false);
    setChangesSummary(null);
    loadResource();
  }, [loadResource]);

  const [errori, setErrori] = useState({});

  function handleChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrori((prev) => {
      if (!prev[key]) return prev;
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });
    setFromCv((prev) => {
      if (!prev.has(key)) return prev;
      const rest = new Set(prev);
      rest.delete(key);
      return rest;
    });
  }

  // 14/10 (Marta): un valore proposto dal CV si scarta con «non usare»,
  // senza doverlo cancellare a mano: il campo torna al valore della risorsa
  // (vuoto) e il salvataggio non lo scrive.
  function scartaDaCv(keys) {
    setForm((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = baselineRef.current[k] ?? (k === 'partita_iva' ? null : '');
      return next;
    });
    setFromCv((prev) => {
      const rest = new Set(prev);
      for (const k of keys) rest.delete(k);
      return rest;
    });
  }

  // Etichetta accanto ai campi precompilati dal CV e non ancora confermati.
  function cvBadge(key) {
    if (!fromCv.has(key)) return null;
    return (
      <span style={{ marginLeft: '6px', fontSize: '10.5px', fontWeight: 500 }}>
        <span
          title={daCv?.cvid ? `Valore letto dal CV Altamira ${daCv.cvid}: diventa effettivo al salvataggio` : undefined}
          style={{ color: 'var(--ds-warning, #b45309)' }}
        >
          dal CV, da confermare
        </span>
        {' · '}
        <button
          type="button"
          onClick={() => scartaDaCv([key])}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '10.5px', color: 'var(--ds-txt-2)', textDecoration: 'underline' }}
        >
          non usare
        </button>
      </span>
    );
  }

  async function handleSave() {
    const err = validaCampi(form);
    setErrori(err);
    if (Object.keys(err).length > 0) {
      setResult({ ok: false, message: 'Correggi i campi evidenziati: nessun dato è stato salvato.' });
      return;
    }
    setSaving(true);
    setResult(null);
    const payload = buildPayload(form);
    const changes = diffChanges(baselineRef.current, payload);
    // Seniority: inviata solo se cambiata e valorizzata (16/10).
    if (form.seniority && form.seniority !== baselineRef.current.seniority) {
      payload.seniority = form.seniority;
      changes.push({ key: 'seniority', label: 'Seniority', before: baselineRef.current.seniority, after: form.seniority });
    }
    // Competenze e settori: inviati solo se cambiati (16/10).
    const competenzePrima = listaTesti(baselineRef.current.competenze);
    const competenzeDopo = listaTesti(form.competenze);
    if (!stessiTesti(competenzeDopo, competenzePrima)) {
      payload.competenze = competenzeDopo;
      changes.push({ key: 'competenze', label: 'Competenze', before: competenzePrima, after: competenzeDopo });
    }
    const settoriPrima = baselineRef.current.settori || [];
    const settoriDopo = form.settori || [];
    if (!stessiCodici(settoriDopo, settoriPrima)) {
      payload.settori = settoriDopo;
      const nomi = (l) => l.map((c) => nomeLeggibile(filtri?.settori, c) || 'settore fuori tassonomia');
      changes.push({ key: 'settori', label: 'Settori', before: nomi(settoriPrima), after: nomi(settoriDopo) });
    }
    try {
      await kmClient.patch(`/resources/${encodeURIComponent(resourceId)}`, payload);
      setResult({ ok: true, message: 'Dati anagrafici aggiornati.' });
      setChangesSummary(changes);
      setEditing(false);
      loadResource();
    } catch (err) {
      setResult({ ok: false, message: err?.detail || 'Errore durante il salvataggio.' });
    } finally {
      setSaving(false);
    }
  }

  if (!resourceId) return null;
  if (status === 'idle' || status === 'loading') {
    return <div style={{ fontSize: '13px', color: 'var(--ds-txt-2)' }}>Caricamento dati anagrafici…</div>;
  }
  if (status === 'not_tracked') {
    return (
      <div style={{ fontSize: '13px', color: 'var(--ds-txt-2)' }}>
        Questa risorsa non è ancora collegata a una scheda KM: i dati anagrafici e commerciali
        non sono disponibili finché non lo è.
      </div>
    );
  }
  if (status === 'error' && !editing) {
    return (
      <div style={{ fontSize: '13px', color: 'var(--ds-error, #b91c1c)' }}>
        {result?.message || 'Errore nel caricamento dei dati anagrafici.'}
      </div>
    );
  }

  const lastUpdateLabel = lastUpdate?.il
    ? `Ultimo aggiornamento HR: ${formatTimestamp(lastUpdate.il)}${lastUpdate.da ? ` (utente #${lastUpdate.da})` : ''}`
    : 'Ultimo aggiornamento HR: nessuno finora';

  // UC-CD-01, flusso alternativo 1 (AF p.32): dettaglio integralmente in
  // sola lettura per R1/R4, azione di modifica non disponibile.
  const soloLettura = cicloVita === 'R1' || cicloVita === 'R4';
  const soloLetturaLabel = STATE_LABELS[cicloVita] || cicloVita;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ fontSize: '13px', color: 'var(--ds-txt-2)' }}>
          Dati mantenuti da HR — separati dal profilo dichiarato dalla risorsa.
        </span>
        {!editing && !soloLettura && (
          <button className="ds-btn" onClick={() => setEditing(true)}>
            Modifica dati anagrafici
          </button>
        )}
      </div>

      {soloLettura && (
        <div style={{ fontSize: '12px', color: 'var(--ds-txt-2)', marginBottom: '10px' }}>
          Sola lettura: risorsa in stato {soloLetturaLabel} — la modifica dei dati anagrafici non è disponibile per questo stato.
        </div>
      )}

      <div style={{ fontSize: '12px', color: 'var(--ds-txt-2)', marginBottom: '10px' }}>
        {lastUpdateLabel}
      </div>

      {editing && !soloLettura && (
        <>
          {fromCv.size > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--ds-txt-2)', marginBottom: '10px' }}>
              {fromCv.size === 1 ? '1 campo vuoto è precompilato' : `${fromCv.size} campi vuoti sono precompilati`} con i dati del CV
              {daCv?.cvid ? ` (scheda Altamira ${daCv.cvid})` : ''}: verificali; diventano dati della risorsa solo salvando.
              {' '}Quelli che non vuoi tenere: «non usare» accanto al campo, oppure{' '}
              <button
                type="button"
                onClick={() => scartaDaCv([...fromCv])}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: 'var(--ds-txt-2)', textDecoration: 'underline' }}
              >
                non usare nessun dato dal CV
              </button>.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {Object.entries(FIELD_LABELS).map(([key, label]) => (
              <div key={key}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--ds-txt-2)' }}>
                  {label}
                  {cvBadge(key)}
                </label>
                {key === 'note_libere' ? (
                  <textarea
                    className="ds-textarea"
                    rows={3}
                    value={form[key] ?? ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    inputMode={NUMERIC_FIELDS.has(key) ? 'decimal' : undefined}
                    className="ds-input"
                    value={form[key] ?? ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    aria-invalid={errori[key] ? 'true' : undefined}
                    style={errori[key] ? { borderColor: 'var(--ds-error, #b91c1c)', boxShadow: '0 0 0 1px var(--ds-error, #b91c1c)' } : undefined}
                  />
                )}
                {errori[key] && (
                  <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--ds-error, #b91c1c)' }}>{errori[key]}</div>
                )}
                {CV_TEXT_HINT[key] && daCv?.[CV_TEXT_HINT[key]] && isEmptyValue(form[key]) && (
                  <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--ds-txt-2)' }}>
                    Dal CV: «{daCv[CV_TEXT_HINT[key]]}» — non è un importo numerico, inserirlo a mano se confermato.
                  </div>
                )}
              </div>
            ))}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--ds-txt-2)' }}>
                Seniority
              </label>
              {/* Dato del profilo: «Non indicata» non viene inviata; una volta
                  impostata si può cambiare ma non svuotare da qui. */}
              <select
                className="ds-input"
                value={form.seniority || ''}
                onChange={(e) => handleChange('seniority', e.target.value)}
              >
                <option value="" disabled={Boolean(baselineRef.current.seniority)}>Non indicata</option>
                {SENIORITY_OPZIONI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--ds-txt-2)' }}>
                Competenze (separate da virgola)
              </label>
              <input
                type="text"
                className="ds-input"
                value={form.competenze ?? ''}
                onChange={(e) => handleChange('competenze', e.target.value)}
                aria-invalid={errori.competenze ? 'true' : undefined}
                style={errori.competenze ? { borderColor: 'var(--ds-error, #b91c1c)', boxShadow: '0 0 0 1px var(--ds-error, #b91c1c)' } : undefined}
              />
              {errori.competenze && (
                <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--ds-error, #b91c1c)' }}>{errori.competenze}</div>
              )}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--ds-txt-2)' }}>
                Settori
              </label>
              <SelettoreSettori
                albero={filtri?.settori}
                valori={form.settori || []}
                onChange={(v) => handleChange('settori', v)}
              />
              <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--ds-txt-2)' }}>
                Seniority, competenze e settori sono dati del profilo: se li modifichi viene salvata una nuova versione del profilo.
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--ds-txt-2)' }}>
                Titolare di partita IVA
                {cvBadge('partita_iva')}
              </label>
              {/* Tre valori: «Non indicato» è null e non viene inviato,
                  così un dato ignoto non diventa «No» al salvataggio. */}
              <select
                className="ds-input"
                value={form.partita_iva === true ? 'si' : form.partita_iva === false ? 'no' : ''}
                onChange={(e) => handleChange('partita_iva', e.target.value === 'si' ? true : e.target.value === 'no' ? false : null)}
              >
                <option value="">Non indicato</option>
                <option value="si">Sì</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button className="ds-btn" onClick={() => { setEditing(false); loadResource(); }} disabled={saving}>
              Annulla
            </button>
            <button className="ds-btn ds-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva dati anagrafici'}
            </button>
          </div>
        </>
      )}

      {result && (
        <div style={{ marginTop: '10px', fontSize: '13px', color: result.ok ? 'var(--ds-success, #15803d)' : 'var(--ds-error, #b91c1c)' }}>
          {result.ok ? '✓ ' : '✗ '}{result.message}
        </div>
      )}

      {result?.ok && changesSummary && (
        changesSummary.length > 0 ? (
          <div style={{ marginTop: '8px', padding: '10px', borderRadius: 'var(--ds-radius-sm)', border: '0.5px solid var(--ds-border)', background: 'var(--ds-surface-2)' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ds-txt-2)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '6px' }}>
              Campi modificati in questo salvataggio
            </div>
            {changesSummary.map((c) => (
              <div key={c.key} style={{ fontSize: '12.5px', color: 'var(--ds-txt)', marginBottom: '3px' }}>
                <strong>{c.label}:</strong> {displayValue(c.key, c.before)} → {displayValue(c.key, c.after)}
              </div>
            ))}
            <div style={{ fontSize: '11px', color: 'var(--ds-txt-2)', marginTop: '6px' }}>
              Riepilogo calcolato solo in questa sessione — non è salvato: chi riapre la scheda in seguito vede
              solo l'ultimo valore, non lo storico dei cambi.
            </div>
          </div>
        ) : (
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ds-txt-2)' }}>
            Nessun campo risulta cambiato rispetto ai valori precedenti.
          </div>
        )
      )}
    </div>
  );
}
